import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { useNavigate } from 'react-router-dom';
import { skillstreeApi } from '@/services/api';
import { LightBulbIcon } from '@heroicons/react/24/outline';
import { toast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/UserContext';
import { subscriptionService } from '@/services/subscriptionService';
import { dashboardService } from '@/services/dashboardService';
import './OnboardingModal.css';

interface CoreCategory {
  id: number;
  name: string;
  slug: string;
  description?: string | null;
}

interface SkillstreeModule {
  code: string;
  description: string;
  isLocked?: boolean;
  image_url?: string;
  is_free?: boolean;
}

interface Skillstree {
  skillstree_id: number;
  skillstree_name: string;
  description: string;
  duration: number;
  image_url?: string;
  core_category_name: string;
  modules?: SkillstreeModule[];
  is_enrolled?: boolean;
}

interface OnboardingModalProps {
  open: boolean;
  onClose: () => void;
  onSkip: () => void;
  onContinue: (categoryId: number, skillstreeId: number) => void;
}

const OnboardingModal: React.FC<OnboardingModalProps> = ({
  open,
  onClose,
  onSkip,
  onContinue,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3>(1);
  const [categories, setCategories] = useState<CoreCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [selectedCategorySlug, setSelectedCategorySlug] = useState<string | null>(null);
  const [selectedCategoryName, setSelectedCategoryName] = useState<string | null>(null);
  const [skillstrees, setSkillstrees] = useState<Skillstree[]>([]);
  const [selectedSkillstrees, setSelectedSkillstrees] = useState<number[]>([]);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [isFreeTrial, setIsFreeTrial] = useState(false);
  const [maxSelections, setMaxSelections] = useState(1);
  const [maxSlots, setMaxSlots] = useState(3);
  const [currentActiveEnrollments, setCurrentActiveEnrollments] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingSkillstrees, setLoadingSkillstrees] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailSkillstree, setDetailSkillstree] = useState<Skillstree | null>(null);
  const detailScrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [skipConfirmOpen, setSkipConfirmOpen] = useState(false);
  const [readyToBeginOpen, setReadyToBeginOpen] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const [enrollmentError, setEnrollmentError] = useState<string | null>(null);
  const CARD_WIDTH = 221.66;
  const CARD_GAP = 24;
  const CARDS_VISIBLE = 3;
  const CONTAINER_WIDTH = CARDS_VISIBLE * CARD_WIDTH + (CARDS_VISIBLE - 1) * CARD_GAP;

  // Check subscription status and current enrollments when modal opens
  useEffect(() => {
    if (open) {
      checkSubscriptionStatus();
      fetchCurrentEnrollments();
    }
  }, [open]);

  const fetchCurrentEnrollments = async () => {
    try {
      const profileResponse = await dashboardService.getUserProfile();
      const profileData = (profileResponse?.data as any)?.data as any;
      const profileSkillstrees = profileData?.skillstrees as any;
      
      // Calculate active (non-completed) enrolled pathways count
      const enrolledSkillstrees = profileSkillstrees?.enrolled || [];
      const activeEnrollments = enrolledSkillstrees.filter((st: any) => !st.is_completed).length;
      
      // Extract enrolled skillstree IDs
      const enrolledIds = enrolledSkillstrees.map((st: any) => st.id);
      console.log('Enrolled Skillstree IDs:', enrolledIds);
      
      // Update skilltrees list to mark enrolled ones
      setSkillstrees(prev => prev.map(st => ({
        ...st,
        is_enrolled: enrolledIds.includes(st.skillstree_id)
      })));
      
      setCurrentActiveEnrollments(activeEnrollments);
    } catch (error) {
      console.error('Failed to fetch current enrollments:', error);
      setCurrentActiveEnrollments(0);
    }
  };

  const checkSubscriptionStatus = async () => {
    try {
      // First, try to get subscription status from profile API (more reliable)
      try {
        const profileResponse = await dashboardService.getUserProfile();
        const profileData = (profileResponse?.data as any)?.data as any;
        const subscription = profileData?.subscription;
        
        if (subscription && subscription.is_active) {
          const isFreeTrialSubscription = subscription.is_free_trial === true;
          setHasSubscription(true);
          setIsFreeTrial(isFreeTrialSubscription);
          // Set max slots: 3 for paid subscriptions, 1 for free trial or no subscription
          const maxSlotsValue = !isFreeTrialSubscription ? 3 : 1;
          setMaxSlots(maxSlotsValue);
          return;
        }
      } catch (profileError) {
        console.warn('Failed to get subscription from profile, falling back to subscription service:', profileError);
      }
      
      // Fallback to subscription service
      const response = await subscriptionService.getUserSubscriptions();
      const activeSubscriptions = response.subscriptions?.filter(sub => sub.is_active) || [];
      const hasActiveSubscription = activeSubscriptions.length > 0;
      
      // Check if any active subscription is a Free Trial
      // Check package name, price, and requires_payment flag
      const hasFreeTrial = activeSubscriptions.some(sub => {
        const packageName = sub.package?.name?.toLowerCase() || '';
        const packagePrice = typeof sub.package?.price === 'string' 
          ? parseFloat(sub.package.price) 
          : (sub.package?.price || 0);
        const requiresPayment = sub.package?.requires_payment !== false; // Default to true if not specified
        
        // Consider it a free trial if:
        // 1. Package name includes "free trial", OR
        // 2. Price is 0 and doesn't require payment
        return packageName.includes('free trial') || 
               (packagePrice === 0 && !requiresPayment);
      });
      
      setHasSubscription(hasActiveSubscription);
      setIsFreeTrial(hasFreeTrial);
      
      // Set max slots based on subscription
      // maxSelections will be calculated by useEffect once currentActiveEnrollments is loaded
      const maxSlotsValue = hasActiveSubscription && !hasFreeTrial ? 3 : 1;
      setMaxSlots(maxSlotsValue);
    } catch (error) {
      console.error('Failed to check subscription status:', error);
      // Default to no subscription if check fails
      setHasSubscription(false);
      setIsFreeTrial(false);
      setMaxSlots(1);
      setMaxSelections(1);
    }
  };

  // Update maxSelections when currentActiveEnrollments changes
  useEffect(() => {
    const availableSelections = Math.max(0, maxSlots - currentActiveEnrollments);
    setMaxSelections(availableSelections);
  }, [currentActiveEnrollments, maxSlots]);

  useEffect(() => {
    if (open) {
      // Reset to step 1 when modal opens
      setCurrentStep(1);
      setSelectedCategory(null);
      setSelectedSkillstrees([]);
      setSkillstrees([]);
      setCarouselIndex(0);
      setIsFreeTrial(false);
      fetchCategories();
    }
  }, [open]);

  const handlePrevCarousel = () => {
    setCarouselIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNextCarousel = () => {
    const maxIndex = Math.max(0, skillstrees.length - 3);
    setCarouselIndex((prev) => Math.min(maxIndex, prev + 1));
  };

  // Mapping functions for category images and descriptions (fallback if API doesn't provide them)
  // Using ID-based mapping for stability - IDs don't change even if name/slug changes
  const getCategoryImage = (id: number): string => {
    const imageMap: Record<number, string> = {
      1: '/big_data.jpg',           // Big Data
      2: '/cloud_computing.jpg',    // Cloud Computing
      3: '/digital_design.jpg',     // Digital Product Design
      4: '/digital_marketing.jpg',  // Product Marketing (or Digital Marketing)
      5: '/cyber_security.jpg',     // Cybersecurity
    };
    return imageMap[id] || '/digital_marketing.jpg'; // Default fallback
  };

  const getCategoryDescription = (id: number, name: string): string => {
    const descriptionMap: Record<number, string> = {
      1: 'Master data analytics, processing, and visualization to make data-driven decisions.',
      2: 'Build scalable applications with AWS, Azure, and Google Cloud infrastructure.',
      3: 'Create stunning UX/UI designs and bring your creative vision to life.',
      4: 'Learn product marketing strategies, go-to-market planning, and customer acquisition to drive product success.',
      5: 'Protect systems and networks from digital attacks, threats, and vulnerabilities.',
    };
    return descriptionMap[id] || `Explore ${name} and build skills that matter.`;
  };

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await skillstreeApi.getCoreCategories();
      const data = (response.data as any)?.data || [];
      setCategories(data);
    } catch (error) {
      console.error('Failed to fetch core categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCategorySelect = (categoryId: number) => {
    setSelectedCategory(categoryId);
    // Find category from API categories
    const category = categories.find(cat => cat.id === categoryId);
    if (category) {
      setSelectedCategorySlug(category.slug);
      setSelectedCategoryName(category.name);
    }
    // Reset carousel index when selecting a new category
    setCarouselIndex(0);
  };

  const handleContinue = async () => {
    if (currentStep === 1 && selectedCategory && selectedCategorySlug) {
      // Move to step 2 and fetch skillstrees
      setLoadingSkillstrees(true);
      try {
        const response = await skillstreeApi.getCoreCategoryWithSkills(selectedCategorySlug);
        const apiData = (response.data as any)?.data;
        if (apiData && apiData.skillstrees) {
          const skillstreesData: Skillstree[] = apiData.skillstrees.map((st: any) => ({
            skillstree_id: st.skillstree_id,
            skillstree_name: st.skillstree_name,
            description: st.description || '',
            duration: st.duration || 0,
            image_url: st.image_url,
            core_category_name: st.core_category_name || selectedCategoryName || '',
            is_enrolled: false, // Initialize as false
            modules: (st.versions?.[0]?.micro_skills || []).map((ms: any, index: number) => ({
              code: [ms.unique_code, ms.title].filter(Boolean).join(' ').trim(),
              description: ms.name || ms.description || 'Micro skill',
              isLocked: index >= 3 || ms.is_free === false,
              image_url: ms.image_url,
              is_free: ms.is_free,
            })),
          }));
          setSkillstrees(skillstreesData);
          setCarouselIndex(0);
          setCurrentStep(2);
          
          // Fetch current enrollments to update the is_enrolled status
          fetchCurrentEnrollments();
        }
      } catch (error) {
        console.error('Failed to fetch skillstrees:', error);
      } finally {
        setLoadingSkillstrees(false);
      }
    } else if (currentStep === 2 && selectedSkillstrees.length > 0) {
      setCurrentStep(3);
    }
  };

  const handleBack = () => {
    if (currentStep === 2) {
      setCurrentStep(1);
      setSelectedSkillstrees([]);
      setSkillstrees([]);
      setCarouselIndex(0); // Reset carousel when going back to step 1
    } else if (currentStep === 3) {
      setCurrentStep(2);
    }
  };

  const handleSkillstreeSelect = (skillstreeId: number) => {
    // Check if skillstree is already enrolled
    const skillstree = skillstrees.find(st => st.skillstree_id === skillstreeId);
    if (skillstree?.is_enrolled) {
      toast({
        title: "Already Enrolled",
        description: "You are already enrolled in this skillstree.",
        variant: "destructive",
      });
      return;
    }
    
    setSelectedSkillstrees((prev) => {
      // If already selected, remove it
      if (prev.includes(skillstreeId)) {
        return prev.filter(id => id !== skillstreeId);
      }
      // If not selected and under limit, add it
      if (prev.length < maxSelections) {
        return [...prev, skillstreeId];
      }
      // If at limit, show toast and don't add
      if (prev.length >= maxSelections) {
        const remainingSlots = maxSlots - currentActiveEnrollments - prev.length;
        toast({
          title: "Selection Limit Reached",
          description: remainingSlots === 0
            ? `You have reached your limit of ${maxSlots} active skillstree${maxSlots > 1 ? 's' : ''}. Complete existing skillstrees to free up slots.`
            : hasSubscription 
              ? `You can only select ${maxSelections} more skillstree${maxSelections > 1 ? 's' : ''} (${currentActiveEnrollments} already active).`
              : "You can only select 1 skillstree with the free plan. Upgrade to Skillstree Plus to select up to 3.",
          variant: "destructive",
        });
      }
      return prev;
    });
  };

  const getDisplayModules = (skillstree: Skillstree | null): SkillstreeModule[] => {
    if (!skillstree) return [];
    const modules = skillstree.modules || [];
    const processed = modules.map((module, index) => ({
      ...module,
      // Lock if index >= 3 OR if is_free is false
      isLocked: module.isLocked ?? (index >= 3 || module.is_free === false),
    }));
    // Return only the actual modules, no placeholders
    return processed;
  };

  const handleViewDetail = (skillstree: Skillstree) => {
    setDetailSkillstree(skillstree);
    setDetailModalOpen(true);
  };

  const closeDetailModal = () => {
    setDetailModalOpen(false);
    setDetailSkillstree(null);
  };

  const checkScrollButtons = () => {
    if (detailScrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = detailScrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  const scrollDetailLeft = () => {
    if (detailScrollRef.current) {
      detailScrollRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };

  const scrollDetailRight = () => {
    if (detailScrollRef.current) {
      detailScrollRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    if (detailModalOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      
      // Check scroll buttons after modal opens
      setTimeout(() => {
        checkScrollButtons();
      }, 100);
      
      // Add scroll listener
      const scrollContainer = detailScrollRef.current;
      if (scrollContainer) {
        scrollContainer.addEventListener('scroll', checkScrollButtons);
      }
      
      return () => {
        document.body.style.overflow = originalOverflow;
        if (scrollContainer) {
          scrollContainer.removeEventListener('scroll', checkScrollButtons);
        }
      };
    }
  }, [detailModalOpen]);

  const progressSteps = [
    { id: 1, label: 'Core Category' },
    { id: 2, label: 'Skillstree' },
    { id: 3, label: 'Confirm Enrollment' },
  ];

  const displayCategories = categories.map(cat => ({
    ...cat,
    description: cat.description || getCategoryDescription(cat.id, cat.name),
    image: getCategoryImage(cat.id),
  }));

  return (
    <Dialog open={open} onOpenChange={() => {
      // Prevent closing by clicking outside - user must use skip or continue button
    }}>
      <DialogContent 
        className={`onboarding-modal-dialog w-full max-w-[1200px] bg-white rounded-2xl p-0 [&>button]:hidden flex flex-col max-h-[95vh] ${detailModalOpen || skipConfirmOpen || readyToBeginOpen ? 'pointer-events-none' : ''}`}
        onInteractOutside={(e) => {
          // Prevent closing by clicking outside
          e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          // Allow escape key to skip
          e.preventDefault();
          onSkip();
        }}
      >
        <div className="flex flex-col h-full min-h-0 max-h-[95vh] overflow-hidden">
          {/* Header */}
          <div className="flex-shrink-0 px-4 sm:px-8 pt-4 pb-2 border-b border-gray-100 relative">
            <button
              onClick={() => setSkipConfirmOpen(true)}
              className="absolute top-4 sm:top-6 right-4 sm:right-8 text-gray-500 hover:text-gray-700 text-xs sm:text-sm font-medium bg-transparent">
              ▷ Skip Onboarding
            </button>
            <div className="mb-2">
              <DialogTitle className="text-2xl font-bold text-gray-900 mb-1 text-center">
                Start Your Learning Journey
              </DialogTitle>
              <DialogDescription className="text-gray-600 text-center text-sm">
                Choose a pathway that aligns with your career goals and start building skills that matter.
              </DialogDescription>
            </div>

            {/* Progress Indicator */}
            <div className="progress-indicator">
              {progressSteps.map((step, index) => {
                const isCompleted = currentStep > step.id;
                const isCurrent = currentStep === step.id;
                return (
                  <React.Fragment key={step.id}>
                    <div className="progress-step">
                      <div
                        className={`progress-circle ${
                          isCompleted ? 'completed' : ''
                        } ${isCurrent ? 'current' : ''}`}
                      >
                        {isCompleted ? (
                          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        ) : isCurrent ? (
                          <span className="progress-dot" />
                        ) : null}
                      </div>
                      <span className={`onboarding-progress-label ${
                        isCompleted || isCurrent ? 'active' : ''
                      }`}>
                        {step.label}
                      </span>
                    </div>
                    {index < progressSteps.length - 1 && (
                      <div className={`progress-line ${
                        currentStep > step.id ? 'active' : ''
                      }`} />
                    )}
                  </React.Fragment>
                ); 
              })}
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 px-4 sm:px-8 py-3 min-h-0" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', overflowY: 'auto' }}>
            {currentStep === 1 && (
              <>
                <h2 className="text-lg font-bold text-gray-900 mb-3">Select Core Category</h2>
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="text-gray-500">Loading categories...</div>
                  </div>
                ) : displayCategories.length === 0 ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="text-gray-500">No categories available.</div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 justify-items-center">
                    {displayCategories.map((category) => {
                      const isSelected = selectedCategory === category.id;
                      return (
                        <div
                          key={category.id}
                          onClick={() => handleCategorySelect(category.id)}
                          className={`
                            relative cursor-pointer rounded-lg overflow-hidden border-2 transition-all
                            w-full max-w-[330px] h-[162px]
                            ${isSelected 
                              ? 'border-purple-600 shadow-lg ring-2 ring-purple-200' 
                              : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                            }
                          `}
                        >
                          {/* Image */}
                          <div className="w-full h-[70px] bg-gray-200 overflow-hidden">
                            <img
                              src={category.image}
                              alt={category.name}
                              className={`w-full h-full object-cover ${category.image ? 'object-cover' : 'object-contain'}`}
                            />
                          </div>

                          {/* Content */}
                          <div className="p-3 bg-white h-[62px] flex flex-col justify-center">
                            <h3 className="text-base font-bold text-gray-900 mt-5 mb-1">
                              {category.name}
                            </h3>
                            {/* <p className="text-xs text-gray-600">
                              {category.description}
                            </p> */}
                          </div>

                          {/* Selection Indicator */}
                          {isSelected && (
                            <div className="absolute top-2 right-2 w-6 h-6 bg-purple-600 rounded-full flex items-center justify-center">
                              <svg
                                className="w-4 h-4 text-white"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}

            {currentStep === 2 && (
              <div className="flex flex-col h-full min-h-0">
                <div className="flex-shrink-0 mb-4 sm:mb-3 step-2-header">
                  <h2 className="text-lg font-bold text-gray-900">Step 2: Select Skillstree</h2>
                </div>
                {loadingSkillstrees ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="text-gray-500">Loading skillstrees...</div>
                  </div>
                ) : skillstrees.length === 0 ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="text-gray-500">No skillstrees found for this category.</div>
                  </div>
                ) : (
                  <div className="relative mx-auto w-full max-w-full px-2 sm:px-4 md:px-8 lg:px-12 flex-1 min-h-0 flex flex-col justify-start sm:justify-center step-2-carousel-wrapper" style={{ overflow: 'visible' }}>
                    {/* Navigation Arrows */}
                    {carouselIndex > 0 && (
                      <button
                        onClick={handlePrevCarousel}
                        className="absolute left-1 sm:left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-white shadow-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors"
                        aria-label="Previous"
                      >
                        <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                    )}
                    {carouselIndex < Math.max(0, skillstrees.length - 3) && (
                      <button
                        onClick={handleNextCarousel}
                        className="absolute right-1 sm:right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-white shadow-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors"
                        aria-label="Next"
                      >
                        <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    )}

                    {/* Carousel Container */}
                    <div 
                      className="overflow-x-hidden overflow-y-visible mx-auto flex-shrink-0 w-full step-2-carousel-container" 
                      style={{ 
                        width: `min(100%, ${CONTAINER_WIDTH}px)`,
                        maxWidth: '100%',
                        position: 'relative',
                        isolation: 'isolate'
                      }}
                    >
                      <div 
                        className="flex gap-3 sm:gap-4 md:gap-6 transition-transform duration-300 ease-in-out step-2-carousel-items"
                        style={{ 
                          transform: `translateX(-${carouselIndex * (CARD_WIDTH + CARD_GAP)}px)`,
                          width: `${skillstrees.length * (CARD_WIDTH + CARD_GAP) - CARD_GAP}px`,
                          willChange: 'transform',
                          alignItems: 'flex-start'
                        }}
                      >
                        {skillstrees.map((skillstree) => {
                          const isSelected = selectedSkillstrees.includes(skillstree.skillstree_id);
                          const isAtLimit = selectedSkillstrees.length >= maxSelections && !isSelected;
                          const isEnrolled = skillstree.is_enrolled || false;
                          const hours = Math.round(skillstree.duration / 3600);
                          
                          return (
                            <div
                              key={skillstree.skillstree_id}
                              onClick={() => !isEnrolled && handleSkillstreeSelect(skillstree.skillstree_id)}
                              className={`
                                relative cursor-pointer rounded-xl overflow-hidden border-2 transition-all flex-shrink-0
                                w-full max-w-[280px] sm:w-[200px] md:w-[221.66px] bg-white skillstree-card
                                ${isEnrolled 
                                  ? 'border-gray-300 opacity-60 cursor-not-allowed' 
                                  : isSelected 
                                    ? 'border-purple-600 shadow-xl ring-2 ring-purple-200' 
                                    : isAtLimit
                                    ? 'border-gray-200 opacity-60 cursor-not-allowed'
                                    : 'border-gray-200 hover:border-gray-300 hover:shadow-lg'
                                }
                              `}
                            >
                              {/* Card Content */}
                              <div className="p-3 sm:p-4 md:p-5">
                                {/* Category Label */}
                                <div className="text-xs text-gray-500 mb-1.5 sm:mb-2 md:mb-3">{selectedCategoryName}</div>
                                
                                {/* Hexagonal Badge with Image */}
                                <div className="flex justify-center skillstree-image-container">
                                  <div className="relative skillstree-badge-image">
                                    {skillstree.image_url ? (
                                      <img
                                        src={skillstree.image_url}
                                        alt={skillstree.skillstree_name}
                                        className="w-full h-full object-cover rounded-lg"
                                        style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' }}
                                      />
                                    ) : null}
                                  </div>
                                </div>

                                {/* Duration & Difficulty */}
                                <div className="flex items-center justify-center gap-2 text-xs sm:text-sm text-gray-600 mb-1.5 sm:mb-2 md:mb-3">
                                  <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  <span>{hours || 0} hours</span>
                                  <span>•</span>
                                  <span>Beginner</span>
                                </div>

                                {/* Description */}
                                <p className="skillstree-description">
                                  {skillstree.description || 'Learn essential skills and techniques for this pathway.'}
                                </p>

                                {/* Action Buttons */}
                                <div className="action-buttons-container">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleViewDetail(skillstree);
                                    }}
                                    className="view-detail-button"
                                  >
                                    View Detail
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (!isEnrolled) {
                                        handleSkillstreeSelect(skillstree.skillstree_id);
                                      }
                                    }}
                                    className={`enroll-now-button ${isSelected ? 'selected' : ''} ${isEnrolled ? 'enrolled' : ''}`}
                                    disabled={isEnrolled}
                                  >
                                    {isEnrolled ? 'Enrolled' : (isSelected ? 'Selected' : 'Enroll Now')}
                                  </button>
                                </div>
                              </div>
                              {/* Selection Indicator */}
                              {isSelected && !isEnrolled && (
                                <div className="absolute top-2 right-2 w-6 h-6 bg-purple-600 rounded-full flex items-center justify-center z-10 flex-shrink-0">
                                  <svg
                                    className="w-4 h-4 text-white flex-shrink-0"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M5 13l4 4L19 7"
                                    />
                                  </svg>
                                </div>
                              )}
                              {/* Enrolled Indicator */}
                              {isEnrolled && (
                                <div className="absolute top-2 right-2 w-6 h-6 bg-purple-600 rounded-full flex items-center justify-center z-10 flex-shrink-0">
                                  <svg version="1.1" xmlns="http://www.w3.org/2000/svg" fill="currentColor" className="w-4 h-4 text-white flex-shrink-0">
                                    <path fill="#ffffff" d="M17.707 8.86l-5.756 5.662c-.392.387-2.485 1.8-2.882 1.41s1.052-2.437 1.445-2.824l5.756-5.662a1.023 1.023 0 0 1 1.43.007.983.983 0 0 1 .007 1.407z"/>
                                    <path fill="#ffffff" d="M12.6 0H1.4A1.417 1.417 0 0 0 0 1.43v15.14A1.417 1.417 0 0 0 1.4 18h11.2a1.417 1.417 0 0 0 1.4-1.43v-2.72l-1.39 1.39c-.02.02-1.93 1.76-3.35 1.76a1.22 1.22 0 0 1-.9-.35c-.8-.81-.06-2.08.26-2.63a9.97 9.97 0 0 1 1.16-1.61L14 8.19V1.43A1.417 1.417 0 0 0 12.6 0zM3.03 2H8a1 1 0 0 1 0 2H3.03a1 1 0 0 1 0-2zM7 12.01L3 12a1 1 0 1 1 0-1.99h4a1 1 0 0 1 0 2zM11 8H3a1 1 0 0 1 0-2h8a1 1 0 0 1 0 2z"/>
                                  </svg>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Pagination Dots */}
                    {skillstrees.length > 3 && (
                      <div className="flex justify-center gap-2 mt-4 mb-2 flex-shrink-0 step-2-pagination-dots" style={{ marginTop: '1rem', marginBottom: '0.5rem' }}>
                        {Array.from({ length: Math.max(1, skillstrees.length - 2) }).map((_, index) => {
                          const isActive = carouselIndex === index;
                          return (
                            <button
                              key={index}
                              onClick={() => setCarouselIndex(index)}
                              className={`h-2 rounded-full transition-all ${
                                isActive
                                  ? 'bg-blue-600 w-8'
                                  : 'bg-gray-300 w-2'
                              }`}
                              aria-label={`Go to slide ${index + 1}`}
                            />
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {currentStep === 3 && (
              <div className="confirm-enrollment-step">
                <div className="confirm-enrollment-content">
                  {/* Left Panel - Selected Core Category */}
                  <div className="selected-category-panel">
                    <label className="panel-label text-center">Selected Core Category:</label>
                    <div className="category-card-preview">
                      {(() => {
                        const category = displayCategories.find(cat => cat.id === selectedCategory);
                        return category ? (
                          <div className="category-preview-card">
                            <div className="category-preview-image">
                              <img
                                src={category.image}
                                alt={category.name}
                                className="category-preview-img"
                              />
                              <div className="category-preview-overlay">
                                <span className="category-preview-name">{category.name}</span>
                              </div>
                            </div>
                          </div>
                        ) : null;
                      })()}
                    </div>
                  </div>

                  {/* Right Panel - Skillstree Pathways Enrolled */}
                  <div className="skillstree-enrolled-panel">
                    <label className="panel-label text-center">Skillstree Pathways Enrolled:</label>
                    <div className="skillstree-enrolled-content">
                      {selectedSkillstrees.length > 0 ? (
                        <>
                          <div className="skillstree-badge-container" style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'center' }}>
                            {selectedSkillstrees.map((skillstreeId) => {
                              const selectedSkillstreeData = skillstrees.find(
                                st => st.skillstree_id === skillstreeId
                              );
                              return selectedSkillstreeData ? (
                                <div key={skillstreeId} className="relative w-28 h-28">
                                  {selectedSkillstreeData.image_url ? (
                                    <img
                                      src={selectedSkillstreeData.image_url}
                                      alt={selectedSkillstreeData.skillstree_name}
                                      className="w-full h-full object-cover rounded-lg"
                                      style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' }}
                                    />
                                  ) : null}
                                </div>
                              ) : null;
                            })}
                          </div>
                          {(!hasSubscription || isFreeTrial) && (
                            <>
                              <p className="upgrade-message">
                                You can enroll in 1 pathway with the free plan. Want to unlock more?
                              </p>
                              <button 
                                className="upgrade-button"
                                onClick={() => {
                                  navigate('/profile?tab=subscription');
                                  onClose();
                                }}
                              >
                                <img src="/star_icon.png" alt="Star icon" className="upgrade-star-icon" />
                                Upgrade to Skillstree Plus
                              </button>
                            </>
                          )}
                        </>
                      ) : null}
                    </div>
                  </div>
                </div>

                {/* Bottom Banner */}
                <div>
                  <p className="text-center bottom-banner-text-content">
                  🎯 You're one step closer to mastering in-demand digital skills. Complete this Skillstree to unlock badges and your Digital Passport!
                  </p>
                </div>
                {/* Details */}
                <div className="enrollment-details">
                  <div className="detail-item">
                    <span className="detail-label">Estimated time to Complete:</span>
                    <span className="detail-value">
                      {(() => {
                        const totalHours = selectedSkillstrees.reduce((total, skillstreeId) => {
                          const skillstreeData = skillstrees.find(st => st.skillstree_id === skillstreeId);
                          return total + (skillstreeData ? Math.round(skillstreeData.duration / 3600) : 0);
                        }, 0);
                        return totalHours > 0 ? `${totalHours} hours` : '3 hours';
                      })()}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Outcome:</span>
                    <span className="detail-value">
                      Earn {selectedSkillstrees.length * 4} Digital Badges + {selectedSkillstrees.length} Digital Passport{selectedSkillstrees.length > 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex-shrink-0 bg-white border-t border-gray-100 rounded-b-2xl">
            <div className="px-4 sm:px-6 py-2 flex items-center justify-between">
              {currentStep === 1 && (
                <>
                  <div className="step-progress">
                    {/* <div className="step-pill step-pill-primary">
                      {selectedCategory ? '1/1' : '0/1'}
                    </div>
                    <span className="step-progress-text">Pathway Selected</span> */}
                  </div>
                  <button
                    onClick={handleContinue}
                    disabled={!selectedCategory}
                    className={`
                      px-6 py-2 rounded-lg font-medium transition-all flex items-center gap-2
                      ${selectedCategory
                        ? 'bg-purple-600 text-white hover:bg-purple-700'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }
                    `}
                  >
                    Continue
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>
                </>
              )}
              {currentStep === 2 && (
                <>
                  <button
                    onClick={handleBack}
                    className="back-button-step3"
                  >
                    <img src="/back_icon.svg" alt="Back" className="back-button-icon" />
                    Back
                  </button>
                  <div className="step-right-group">
                    <div className="step-progress">
                      <div className="step-pill step-pill-secondary">
                        {currentActiveEnrollments + selectedSkillstrees.length}/{maxSlots}
                      </div>
                      <span className="step-progress-text">Skillstree Pathways Enrolled</span>
                    </div>
                    <button
                      onClick={handleContinue}
                      disabled={selectedSkillstrees.length === 0}
                      className={`step-next-button ${selectedSkillstrees.length > 0 ? '' : 'disabled'}`}
                    >
                      Next
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </button>
                  </div>
                </>
              )}
              {currentStep === 3 && (
                <>
                  <button
                    onClick={handleBack}
                    className="back-button-step3"
                  >
                    <img src="/back_icon.svg" alt="Back" className="back-button-icon" />
                    Back
                  </button>
                  <button
                    onClick={async () => {
                      if (selectedSkillstrees.length === 0 || enrolling) return;
                      
                      // Clear any previous errors
                      setEnrollmentError(null);
                      
                      try {
                        setEnrolling(true);
                        // Enroll in all selected skillstrees
                        await Promise.all(
                          selectedSkillstrees.map(skillstreeId => skillstreeApi.enroll(skillstreeId))
                        );
                        // After successful enrollment, show the "Ready to Begin" modal
                        setEnrollmentError(null);
                        setReadyToBeginOpen(true);
                      } catch (error: any) {
                        console.error('Failed to enroll in skillstree:', error);
                        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to enroll in skillstree';
                        
                        // Check if it's the enrollment limit error
                        let displayMessage = errorMessage;
                        if (errorMessage.includes('You can only enroll in 1 Skillstrees') || errorMessage.includes('You can only enroll in 1 Skillstree')) {
                          displayMessage = 'You can only enroll in 1 Skillstree with the free plan. Upgrade to Skillstree Plus to unlock more pathways.';
                        }
                        
                        setEnrollmentError(displayMessage);
                        // Show error in a toast notification
                        toast({
                          title: "Enrollment Failed",
                          description: displayMessage,
                          variant: "destructive",
                        });
                      } finally {
                        setEnrolling(false);
                      }
                    }}
                    disabled={enrolling || selectedSkillstrees.length === 0}
                    className="confirm-journey-button"
                  >
                    {enrolling ? 'Enrolling...' : 'Confirm & Begin Your Journey'}
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </>
              )}
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-500 px-4 sm:px-8 pb-1.5 justify-center text-center">
              <span>💡 You can change your selection at any time during the onboarding process</span>
            </div>
          </div>
        </div>
      </DialogContent>

      {detailModalOpen && detailSkillstree && createPortal(
        <div 
          className="skillstree-detail-modal-overlay" 
          role="dialog" 
          aria-modal="true" 
          onClick={(e) => {
            // Only close if clicking directly on the overlay, not during scroll
            if (e.target === e.currentTarget) {
              closeDetailModal();
            }
          }}
        >
          <div className="skillstree-detail-modal" onClick={(e) => e.stopPropagation()}>
            <button className="detail-close-button" onClick={closeDetailModal} aria-label="Close detail modal">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <div className="detail-circles-navigation-wrapper">
              {canScrollLeft && (
                <button 
                  className="detail-scroll-arrow detail-scroll-arrow-left" 
                  onClick={scrollDetailLeft}
                  aria-label="Scroll left"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M7.99967 12.6668L3.33301 8.00016L7.99967 3.3335" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M12.6663 8H3.33301" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              )}
              <div className="detail-circles-container-wrapper">
                <div className="detail-circles-wrapper">
                  <div className="detail-circles-scroll" ref={detailScrollRef}>
                    <div className="detail-circles-container">
                      {getDisplayModules(detailSkillstree).map((module, index, array) => (
                        <div className="detail-circle-wrapper" key={`${module.code || 'locked'}-${index}`}>
                          <div
                            className={`detail-circle ${
                              index < 3
                                ? 'orange'
                                : 'green'
                            }`}
                          >
                            {module.isLocked && (!hasSubscription || isFreeTrial) ? (
                              // Show subscribe message when user doesn't have subscription or has free trial
                              <div className="detail-circle-locked">
                                <svg className="locked-icon" viewBox="0 0 20 20" fill="currentColor">
                                  <path d="M10 2a4 4 0 00-4 4v2H5a1 1 0 00-1 1v7a1 1 0 001 1h10a1 1 0 001-1v-7a1 1 0 00-1-1h-1V6a4 4 0 00-4-4zm-2 6V6a2 2 0 114 0v2H8z" />
                                </svg>
                                <p>Subscribe to <span className="skillstree-plus-text">Skillstree Plus</span> to learn more.</p>
                              </div>
                            ) : (
                              // Show module code normally (for unlocked modules or locked modules when user has paid subscription)
                              <div className="detail-circle-text">
                                <div className="detail-circle-code">{module.code || 'Locked'}</div>
                              </div>
                            )}
                          </div>
                          <div className="detail-circle-connector" />
                        </div>
                      ))}
                      {/* Final blue circle with skillstree name */}
                      <div className="detail-circle-wrapper">
                        <div className="detail-circle blue">
                          <div className="detail-circle-text">
                            <div className="detail-circle-code">{detailSkillstree.skillstree_name}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {canScrollRight && (
                <button 
                  className="detail-scroll-arrow detail-scroll-arrow-right" 
                  onClick={scrollDetailRight}
                  aria-label="Scroll right"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8.00033 3.33317L12.667 7.99984L8.00033 12.6665" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M3.33366 7.99984H12.667" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Skip Confirmation Modal */}
      {skipConfirmOpen && createPortal(
        <div className="skip-confirm-modal-overlay" role="dialog" aria-modal="true" onClick={() => setSkipConfirmOpen(false)}>
          <div className="skip-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="skip-confirm-title">Skip Entire Onboarding?</h2>
            <p className="skip-confirm-description">
              This will skip all steps of the onboarding wizard. You can always access these settings later from your profile to customize your learning path.
            </p>
            <div className="skip-confirm-buttons">
              <button
                className="skip-confirm-button skip-confirm-button-secondary"
                onClick={() => setSkipConfirmOpen(false)}
              >
                Continue Setup
              </button>
              <button
                className="skip-confirm-button skip-confirm-button-primary"
                onClick={() => {
                  setSkipConfirmOpen(false);
                  onSkip();
                }}
              >
                Skip All Steps
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Ready to Begin Modal */}
      {readyToBeginOpen && createPortal(
        <div className="ready-to-begin-modal-overlay" role="dialog" aria-modal="true" onClick={() => {
          setReadyToBeginOpen(false);
          setEnrollmentError(null);
        }}>
          <div className="ready-to-begin-modal" onClick={(e) => e.stopPropagation()}>
            <button 
              className="ready-to-begin-close-button" 
              onClick={() => {
                setReadyToBeginOpen(false);
                setEnrollmentError(null);
              }}
              aria-label="Close modal"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <h2 className="ready-to-begin-title">Ready to Begin?</h2>
            <p className="ready-to-begin-description">
              Your learning journey is about to start! Choose how you'd like to proceed.
            </p>
            {enrollmentError && (
              <div className="ready-to-begin-error">
                <div className="ready-to-begin-error-icon">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M10 6V10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M10 14H10.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div className="ready-to-begin-error-content">
                  <div className="ready-to-begin-error-title">Enrollment Limit Reached</div>
                  <div className="ready-to-begin-error-message">{enrollmentError}</div>
                </div>
              </div>
            )}
            <div className="ready-to-begin-buttons">
              <button
                className="ready-to-begin-button ready-to-begin-button-secondary"
                onClick={() => {
                  setReadyToBeginOpen(false);
                  onClose();
                  navigate('/dashboard');
                }}
              >
                Go to Dashboard
              </button>
              <button
                className="ready-to-begin-button ready-to-begin-button-primary"
                onClick={() => {
                  setReadyToBeginOpen(false);
                  if (selectedSkillstrees.length > 0 && selectedCategory) {
                    // Pass both categoryId and skillstreeId to onContinue
                    onContinue(selectedCategory, selectedSkillstrees[0]);
                  }
                }}
              >
                Start Learning
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </Dialog>
  );
};

export default OnboardingModal;

